import {useState} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';

import Format from '~/lib/format';

import sy from '~/styles';
import {
  Text,
  Pressable,
  Dialog,
  ActionButton,
  Grouping,
} from '~/components/controls';
import Actionsheet, {Title, Actions} from '~/components/controls/Actionsheet';
import {
  ROB_TIRE_SWAP_CODE,
  ROB_TIRE_REPLACE_CODE,
  ROB_TIRE_REPAIR_CODE,
  TIRE_STATE,
  TIRE_STATE_LABEL,
  TIRE_SOURCE,
  TIRE_SOURCE_LABEL,
  LOCATION_TEXT,
  TIRE_REPAIR,
} from '~/types/rob';

import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import ArchiveIcon from '~/images/md-icons/archive/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import ReceiptLongIcon from '~/images/md-icons/receipt_long/materialicons/24px.svg';

import {SeasonIndicator, Profile} from '../shared';

const UNMOUNT_ACTION = {
  None: 'None',
  Remove: 'Remove',
  Store: 'Store',
  Repair: 'Repair',
};

const Unmount_Actions = [
  {
    key: UNMOUNT_ACTION.None,
    label: 'Geen actie',
    icon: <ListIcon />,
  },
  {
    key: UNMOUNT_ACTION.Remove,
    label: 'Afvoeren',
    icon: <DeleteIcon />,
    sla_items: [
      {
        rob_code: ROB_TIRE_REPLACE_CODE,
        default_source: TIRE_SOURCE.New,
        default_tire_state: TIRE_STATE.Worn,
        label: 'Bandvervanging',
        action: 'Current_Replace',
      },
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: TIRE_SOURCE.New,
        default_stored_source: TIRE_SOURCE.Stored,
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel zonder velg',
        action: 'Current_Remove',
      },
    ],
  },
  {
    key: UNMOUNT_ACTION.Store,
    label: 'Opslaan',
    icon: <ArchiveIcon />,
    sla_items: [
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: TIRE_SOURCE.New,
        default_stored_source: TIRE_SOURCE.Stored,
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel zonder velg',
        action: 'Current_Store',
      },
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: TIRE_SOURCE.New,
        default_stored_source: TIRE_SOURCE.Stored,
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel met velg',
        action: 'Current_StoreWheel',
        is_wheel: true,
      },
    ],
  },
  {
    key: UNMOUNT_ACTION.Repair,
    label: 'Bewerken',
    icon: <TireRepairIcon />,
    sla_items: [
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Repareren',
        action: TIRE_REPAIR.Repareren,
        operation_code: '03',
      },
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Balanceren',
        action: TIRE_REPAIR.Balanceren,
        operation_code: '20',
      },
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Omwisselen',
        action: TIRE_REPAIR.Wielomwisselen,
        operation_code: '28',
      },
    ],
  },
];

const TIRE_COST = {
  service: 'service',
  disposalFee: 'disposalFee',
  surchargeRunOnFlat: 'surchargeRunOnFlat',
  storageCosts: 'storageCosts',
  surchargeTireSource: 'surchargeTireSource',
};

const COST_LABEL = {
  [TIRE_COST.service]: 'Servicetarief',
  [TIRE_COST.disposalFee]: 'Verwijderingsbijdrage',
  [TIRE_COST.surchargeRunOnFlat]: 'ROF toeslag',
  [TIRE_COST.storageCosts]: 'Opslagkosten',
  [TIRE_COST.surchargeTireSource]: 'Toeslag uit voorraad LM',
};

const MountedTire = ({mounted, location}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4']]}>
      <Profile
        location={location}
        widths={{[location]: mounted.profile_width}}
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>{mounted.title}</Text>
            <SeasonIndicator indicator={mounted.seasonIndicator} />
          </View>
          <Text style={[sy.small, sy['text-lightgray'], sy.truncate]}>
            {mounted.description}
          </Text>
        </View>
      </View>
    </View>
  </View>
);

const StoredTire = ({stored, location}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4'], sy['pr-4']]}>
      <Profile
        location={location}
        widths={{[location]: stored.profile_width}}
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>{stored.title}</Text>
            <SeasonIndicator indicator={stored?.seasonIndicator} />
          </View>
          <Text style={[sy.small, sy['text-lightgray'], sy.truncate]}>
            {stored.description}
          </Text>
        </View>
      </View>
    </View>
  </View>
);

const NewTire = ({newTire, location}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4'], sy['pr-4']]}>
      <Profile
        location={location}
        widths={{[location]: newTire.profile_width}}
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>{newTire.title}</Text>
            <SeasonIndicator indicator={newTire.seasonIndicator} />
          </View>
          <Text style={[sy.small, sy['text-lightgray'], sy.truncate]}>
            {newTire.description}
          </Text>
        </View>
        <View style={[sy['pl-2']]}>
          <Text style={sy.smallRegular}>
            {Format.price(newTire.price - newTire.discount)}
          </Text>
        </View>
      </View>
    </View>
  </View>
);

const ActionsActionsheet = (props) => {
  const {onDismiss} = props;

  const [showCostsPreviewDialog, setShowCostsPreviewDialog] = useState(false);

  const find_action = (action) => {
    if (!action) {
      return null;
    }

    return Unmount_Actions.map((item) => {
      const sla_item = item.sla_items?.find(
        (sla_item) => sla_item.action === action,
      );

      if (sla_item) {
        return {
          key: item.key,
          tireAction: sla_item,
        };
      }

      return null;
    })
      .filter(Boolean)
      .shift();
  };

  const {tireAction, key: unmountAction} =
    find_action(props.tire.repairs?.[0]) ?? find_action(props.tire.action);

  const tireState =
    props.tire.attributes?.tire_state ?? tireAction?.default_tire_state;
  const storedTireState = props.tire.stored.tire_state ?? TIRE_STATE.Worn;
  const mountedTireSource =
    (props.tire.new_tire
      ? props.tire.new_tire.source ?? TIRE_SOURCE.New
      : null) ??
    (tireAction.default_stored_source && props.tire?.stored?.rob_tire_id
      ? tireAction.default_stored_source
      : tireAction.default_source);

  const service_cost =
    props.tire.service_cost[tireAction.label] ??
    props.tire.service_cost.service;

  const part_costs =
    Object.values(props.tire.service_cost).reduce(
      (acc, value) => acc + value,
      0,
    ) - service_cost;

  const amount = service_cost + part_costs + (props.tire.new_tire?.price ?? 0);

  return (
    <>
      <Actionsheet visible={true} dismissable={false} onDismiss={onDismiss}>
        <Title>
          <View
            style={[
              sy['flex-1'],
              sy['flex-row'],
              sy['items-between'],
              sy['justify-between'],
            ]}>
            <Text style={[sy.large, {fontSize: 20}]}>
              Band&nbsp;
              {(LOCATION_TEXT[props.location] ?? props.location).toLowerCase()}
            </Text>
          </View>
        </Title>
        <Divider style={sy['bg-gray88']} />
        <MountedTire
          mounted={props.tire.attributes}
          location={props.location}
        />
        <Divider style={sy['bg-gray88']} />
        {props.tire.attributes.rob_tire_id && (
          <>
            {Unmount_Actions.map((action) => {
              if (action.key !== unmountAction) {
                return null;
              }

              return (
                <View
                  key={action.key}
                  style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}>
                  {action.icon}
                  <View
                    style={[
                      sy['flex-1'],
                      sy['flex-row'],
                      sy['justify-between'],
                    ]}>
                    <View>
                      <Text>{action.label}</Text>
                      {tireAction && (
                        <Text style={[sy.small, sy['text-lightgray']]}>
                          SLA&nbsp;&middot;&nbsp;
                          {tireAction.label}
                        </Text>
                      )}
                    </View>
                    <Text style={[sy.smallRegular]}>
                      {Format.price(service_cost)}
                    </Text>
                  </View>
                </View>
              );
            })}
            <Divider style={sy['bg-gray88']} />
          </>
        )}
        {tireAction.rob_code === ROB_TIRE_REPLACE_CODE && (
          <>
            <View
              style={[
                sy['p-4'],
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
              ]}>
              <Text style={[sy.mediumBold]}>Reden</Text>
              <Text>{TIRE_STATE_LABEL[tireState]}</Text>
            </View>
            <Divider style={sy['bg-gray88']} />
          </>
        )}
        {mountedTireSource && (
          <>
            <View
              style={[
                sy['p-4'],
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
              ]}>
              <Text style={[sy.mediumBold]}>Artikel</Text>
              <Text>{TIRE_SOURCE_LABEL[mountedTireSource]}</Text>
            </View>
            {mountedTireSource === TIRE_SOURCE.Stored ? (
              <>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <StoredTire
                  stored={props.tire.stored}
                  location={props.location}
                />
              </>
            ) : (
              <>
                {props.tire.new_tire && (
                  <>
                    <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                    <NewTire
                      newTire={props.tire.new_tire}
                      mounted={props.tire.attributes}
                      stored={props.tire.stored}
                      location={props.location}
                      tireAction={tireAction}
                      mountedTireSource={mountedTireSource}
                    />
                  </>
                )}
              </>
            )}
            <Divider style={sy['bg-gray88']} />
          </>
        )}
        {props.tire.stored?.rob_tire_id &&
          mountedTireSource !== TIRE_SOURCE.Stored &&
          tireAction.rob_code === ROB_TIRE_SWAP_CODE && (
            <>
              <View
                style={[
                  sy['p-4'],
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['items-center'],
                ]}>
                <Text style={[sy.mediumBold]}>Opslag afvoeren</Text>
                <Text>{TIRE_STATE_LABEL[storedTireState]}</Text>
              </View>
              <>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <StoredTire
                  stored={props.tire.stored}
                  location={props.location}
                />
              </>
              <Divider style={sy['bg-gray88']} />
            </>
          )}
        {part_costs > 0 && (
          <>
            <View
              style={[
                sy['p-4'],
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
              ]}>
              <Text style={[sy.mediumBold]}>Overige kosten</Text>
              <Text style={[sy.smallRegular]}>{Format.price(part_costs)}</Text>
            </View>
            <Divider style={sy['bg-gray88']} />
          </>
        )}
        <Actions style={[sy['justify-between']]}>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text style={[sy.mediumBold, sy['px-2']]}>
              {Format.price(amount)}
            </Text>
            <Pressable onPress={() => setShowCostsPreviewDialog(true)}>
              <ReceiptLongIcon fill="#4a4a49" />
            </Pressable>
          </View>
          <View style={[sy['flex-row']]}>
            <ActionButton onPress={onDismiss}>Sluiten</ActionButton>
          </View>
        </Actions>
      </Actionsheet>

      <Dialog
        visible={showCostsPreviewDialog}
        title={<Text style={sy.largePlus}>Details</Text>}
        titleIcon={<ReceiptLongIcon fill="#4a4a49" />}
        options={{noPaddingContent: true}}
        onDismiss={() => setShowCostsPreviewDialog(false)}>
        <View style={[sy['pt-4']]}>
          <View style={[sy['px-4'], sy['pb-4'], sy['gap-4']]}>
            {Object.keys(props.tire.service_cost ?? {})
              .sort((a, b) => {
                const label_a = COST_LABEL[a] ?? a;
                const label_b = COST_LABEL[b] ?? b;

                return label_a.localeCompare(label_b);
              })
              .map((key) => (
                <View key={key} style={[sy['flex-row'], sy['justify-between']]}>
                  <Text>{COST_LABEL[key] ?? key}</Text>
                  <Text style={[sy.smallRegular]}>
                    {Format.price(props.tire.service_cost[key])}
                  </Text>
                </View>
              ))}
          </View>
          {props.tire.new_tire && (
            <Grouping header="Band" initial={{expand: true}}>
              <View style={[sy['py-4'], sy['gap-4']]}>
                <View
                  style={[
                    sy['px-4'],
                    sy['flex-row'],
                    sy['justify-between'],
                    sy['gap-4'],
                  ]}>
                  <View style={[sy['flex-1']]}>
                    <Text>{props.tire.new_tire.title}</Text>
                    <Text style={[sy.small, sy['text-lightgray']]}>
                      {props.tire.new_tire.description}
                    </Text>
                  </View>
                  <Text style={[sy.smallRegular]}>
                    {Format.price(props.tire.new_tire.price)}
                  </Text>
                </View>

                {props.tire.new_tire.discount > 0 && (
                  <>
                    <Divider style={sy['bg-gray95']} />
                    <View
                      style={[
                        sy['px-4'],
                        sy['flex-row'],
                        sy['justify-between'],
                        sy['gap-4'],
                      ]}>
                      <View>
                        <Text>Na korting in opdracht</Text>
                        <Text style={[sy.small, sy['text-lightgray']]}>
                          {Format.number(
                            (props.tire.new_tire.discount /
                              props.tire.new_tire.price) *
                              100,
                          )}
                          % &nbsp;&middot;&nbsp;{props.lessor}
                        </Text>
                      </View>
                      <Text style={[sy.smallRegular]}>
                        {Format.price(
                          props.tire.new_tire.price -
                            props.tire.new_tire.discount,
                        )}
                      </Text>
                    </View>
                  </>
                )}
              </View>
            </Grouping>
          )}
        </View>
      </Dialog>
    </>
  );
};

export default ActionsActionsheet;
